import React, { useState } from 'react';
import api from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import { Lock, Eye, EyeOff, CheckCircle2, AlertCircle, KeyRound } from 'lucide-react';

export default function CambiarClaveModal() {
  const { user, refreshUser, logout } = useAuth();
  const [form, setForm] = useState({ password_actual: '', password_nueva: '', password_confirmar: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleGuardar = async () => {
    if (!form.password_actual || !form.password_nueva) {
      setError('Todos los campos son obligatorios');
      return;
    }
    if (form.password_nueva.length < 8) {
      setError('La nueva contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (form.password_nueva !== form.password_confirmar) {
      setError('Las contraseñas nuevas no coinciden');
      return;
    }
    if (form.password_nueva === form.password_actual) {
      setError('La nueva contraseña debe ser diferente a la actual');
      return;
    }
    setLoading(true); setError('');
    try {
      await api.post('/auth/change-password', {
        password_actual: form.password_actual,
        password_nueva: form.password_nueva
      });
      setSuccess(true);
      setTimeout(() => refreshUser(), 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cambiar la contraseña');
    } finally { setLoading(false); }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 999, padding: 20,
      backdropFilter: 'blur(6px)'
    }}>
      <div style={{
        background: 'white', borderRadius: 16, maxWidth: 460, width: '100%',
        padding: 36, boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        animation: 'modalIn .25s ease'
      }}>
        {/* Icono y título */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{
            width: 64, height: 64, background: 'linear-gradient(135deg, #e91e8c, #9c27b0)',
            borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px'
          }}>
            <KeyRound size={28} color="white" />
          </div>
          <h2 style={{ fontSize: '1.3rem', margin: 0 }}>Cambio de contraseña obligatorio</h2>
          <p style={{ marginTop: 8, fontSize: '0.9rem', lineHeight: 1.5 }}>
            Hola, <strong>{user?.nombre}</strong>. Por seguridad, debes establecer tu propia contraseña 
            antes de continuar. Esta acción garantiza que solo tú conoces tu clave de acceso.
          </p>
        </div>

        {success ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <CheckCircle2 size={48} color="var(--color-success)" style={{ margin: '0 auto 12px', display: 'block' }} />
            <h3 style={{ color: 'var(--color-success)', margin: '0 0 8px' }}>¡Contraseña actualizada!</h3>
            <p style={{ fontSize: '0.875rem' }}>Tu contraseña ha sido guardada de forma segura. Accediendo al sistema...</p>
          </div>
        ) : (
          <>
            {error && (
              <div className="alert alert-danger" style={{ marginBottom: 16 }}>
                <AlertCircle size={16} /> {error}
              </div>
            )}

            <div className="form-group">
              <label className="form-label">
                <Lock size={13} style={{ display: 'inline', marginRight: 4 }} />
                Contraseña actual (la que te dieron)
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPass ? 'text' : 'password'}
                  className="form-control"
                  style={{ paddingRight: 40 }}
                  value={form.password_actual}
                  onChange={e => set('password_actual', e.target.value)}
                  placeholder="Tu contraseña actual"
                  autoFocus
                />
                <button type="button" onClick={() => setShowPass(!showPass)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-3)' }}>
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Nueva contraseña</label>
              <input
                type={showPass ? 'text' : 'password'}
                className="form-control"
                value={form.password_nueva}
                onChange={e => set('password_nueva', e.target.value)}
                placeholder="Mínimo 8 caracteres"
              />
              {form.password_nueva && (
                <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {[
                    [form.password_nueva.length >= 8, '8+ caracteres'],
                    [/[A-Z]/.test(form.password_nueva), 'Mayúscula'],
                    [/[0-9]/.test(form.password_nueva), 'Número'],
                    [/[^A-Za-z0-9]/.test(form.password_nueva), 'Símbolo'],
                  ].map(([ok, label]) => (
                    <span key={label} style={{
                      fontSize: '0.72rem', padding: '2px 8px', borderRadius: 20, fontWeight: 600,
                      background: ok ? 'var(--color-success-bg)' : 'var(--color-gray-2)',
                      color: ok ? 'var(--color-success)' : 'var(--color-text-3)'
                    }}>
                      {ok ? '✓' : '○'} {label}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group" style={{ marginBottom: 24 }}>
              <label className="form-label">Confirmar nueva contraseña</label>
              <input
                type={showPass ? 'text' : 'password'}
                className="form-control"
                value={form.password_confirmar}
                onChange={e => set('password_confirmar', e.target.value)}
                placeholder="Repite la nueva contraseña"
                style={{ borderColor: form.password_confirmar && form.password_confirmar !== form.password_nueva ? 'var(--color-danger)' : '' }}
              />
            </div>

            <button
              className="btn btn-primary btn-lg"
              style={{ width: '100%', justifyContent: 'center' }}
              onClick={handleGuardar}
              disabled={loading}
            >
              {loading ? <div className="spinner" style={{ width: 18, height: 18 }} /> : <Lock size={18} />}
              {loading ? 'Guardando...' : 'Establecer mi contraseña personal'}
            </button>

            <button
              className="btn btn-ghost btn-sm"
              style={{ width: '100%', justifyContent: 'center', marginTop: 10, color: 'var(--color-text-3)' }}
              onClick={logout}
            >
              Cerrar sesión
            </button>
          </>
        )}
      </div>
    </div>
  );
}
